import { ActivatedRoute, Router } from '@angular/router';
import { Component, effect, inject, signal, untracked } from '@angular/core';
import { DatasetFormAttachmentComponent } from '@components/dataset/form/attachment/attachment.component';
import { DatasetFormAttachmentCreateComponent } from '@components/dataset/form/attachment/create/create.component';
import { DatasetFormAttachmentDeleteComponent } from '@components/dataset/form/attachment/delete/delete.component';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormControlMarkdownComponent } from '@components/form-control/markdown/markdown.component';
import { GeologicalSampleGeologistComponent } from '@components/geology/sample/form/geologist/form.component';
import { GeologicalSampleLinkComponent } from '@components/geology/sample/form/link/form.component';
import { HttpParams, HttpResourceRef, httpResource } from '@angular/common/http';
import { KeyValuePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule, MatListOption } from '@angular/material/list';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatStepperModule } from '@angular/material/stepper';
import { PromptComponent } from '@components/prompt/prompt.component';
import { ResourcePickerComponent } from '@components/resource-picker/resource-picker.component';
import { environment } from '@environment';

import {
  AttachmentService,
  AuthService,
  GeologicalSampleService,
  NotificationService,
} from '@services';

import {
  Attachment,
  AttachmentListResponse,
  Expeditions,
  GeoJSONToCoordinates,
  GeologicalSample,
  GeologicalSampleDetails,
  Geologists,
  Placename,
  Permission,
  PositionAccuracy,
} from '@models'

@Component({
  selector: 'app-geology-sample-form',
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.scss'],
  imports: [
    FormControlMarkdownComponent,
    KeyValuePipe,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatListOption,
    MatPaginatorModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatStepperModule,
    ReactiveFormsModule,
  ],
})
export class GeologicalSampleFormComponent {
  // Environment
  kinko = environment.kinkoHost;
  geologicalSampleDatasetId = environment.geologicalSampleDatasetId;

  // Enums
  accuracyOpts = PositionAccuracy;
  expeditionList = Expeditions;
  geologists = Geologists;

  // Services
  authService = inject(AuthService);
  attachmentService = inject(AttachmentService);
  geologicalSampleService = inject(GeologicalSampleService);
  notificationService = inject(NotificationService);

  // Routing
  route = inject(ActivatedRoute);
  router = inject(Router);

  // Data
  sample = signal<GeologicalSample>(this.route.snapshot.data['sample']);
  permissions = signal<Permission>(this.route.snapshot.data['permissions']);
  isDraft = signal<boolean>(this.sample().content?.draft || true);
  isFossil = signal<boolean>(this.sample().content?.fossil || false);

  // Dialog
  dialog = inject(MatDialog);

  ///////////
  // FORMS //
  ///////////

  // Form setup
  isLinear = signal(false);

  // General Info

  lithology: FormControl = new FormControl(this.sample().content?.lithology, {});
  description: FormControl = new FormControl(this.sample().content?.description, {});
  expedition: FormControl = new FormControl(this.sample().content?.expedition, {});
  originalSampleName: FormControl = new FormControl(this.sample().content?.originalSampleName, {});
  collectedYear: FormControl = new FormControl(this.sample().content?.collectedYear, {});
  cabinetRef: FormControl = new FormControl(this.sample().content?.cabinetRef, {});
  thinSectionRef: FormControl = new FormControl(this.sample().content?.thinSectionRef, {});

  generalForm: FormGroup = new FormGroup({
    lithology          : this.lithology,
    description        : this.description,
    expedition         : this.expedition,
    originalSampleName : this.originalSampleName,
    collectedYear      : this.collectedYear,
    cabinetRef         : this.cabinetRef,
    thinSectionRef     : this.thinSectionRef,
  });

  // Geographic Info

  placename: FormControl = new FormControl(this.sample().content?.placename, {});

  coords = GeoJSONToCoordinates(
    this.sample().content?.geojson || {type: 'FeatureCollection', features:[]}
  )[0] || [0,0,0];
  longitude: FormControl = new FormControl(this.coords[0], { validators: [
    Validators.min(-180),
    Validators.max(180),
  ]});
  latitude: FormControl = new FormControl(this.coords[1], { validators: [
    Validators.min(-90),
    Validators.max(90),
  ]});
  altitude: FormControl = new FormControl(this.coords[2] || 0, {});
  positionAccuracy: FormControl = new FormControl(this.sample().content?.positionAccuracy, {});

  geoForm: FormGroup = new FormGroup({
    placename: this.placename,
    longitude: this.longitude,
    latitude: this.latitude,
    altitude: this.altitude,
    positionAccuracy: this.positionAccuracy,
  });

  // Placename lookup dialog

  placeLookup() {
    this.dialog.open(ResourcePickerComponent<Placename>, {
      minWidth: '360px',
      width: '480px',
      autoFocus: true,
      data: {
        title: "Placename lookup",
        "description": `Search for a name that matches the collection location.
          The selected placename's coordinates will be used in the form if none
          were provided yet.`,
        label: "Placename",
        placeholder: "Search the NPI placename database",
        endpoint: `${environment.kinkoHost}/placename/?skip=0&take=10&order=created:desc&status=official&status=standardised`,
      }
    }).afterClosed().subscribe((result?: Placename) => {
      if (!result) {
        return;
      }

      // Update the form fields
      this.placename.setValue(result.title);

      // Only update the coordinate fields if all values are 0.
      const coords = result.geometry.coordinates;
      console.log(this.longitude.value)
      if (
        this.longitude.value === 0 &&
        this.latitude.value === 0 &&
        this.altitude.value === 0
      ) {
        this.longitude.setValue(coords[0]);
        this.latitude.setValue(coords[1]);
        this.altitude.setValue(coords[2] || 0);
      }
    });
  }


  /////////////////
  // ATTACHMENTS //
  /////////////////

  // images
  imagePrefix = '/sample/images/'
  imageCount = signal(0);
  imagePage = signal(0);
  imageDefaultPage = 5;
  imagePageSize = signal(this.imageDefaultPage);

  // We make a separate request to get images attached to the record. We use a
  // regex query looking for a string that looks like a prefix ending on
  // '/.+/images/$' rather than the actual prefix. Since the prefix contains
  // the expedition it's not unlikely that someone edits it by accident or
  // maybe an update fails and the prefix goes out of sync. This way we have
  // the best chance to return a correct file list giving people to edit/see
  // the mistakes.
  //
  // NOTE! On the item page we do use the prefix. Hopefully this makes it easy
  // to see to the user that they broke something that needs to be fixed when
  // their file disappears.
  imageList = httpResource<AttachmentListResponse>(() => {
    const count = untracked(() => this.imageCount() === 0 || this.imagePage() === 0);
    const httpParams = new HttpParams()
      .set('skip', this.imagePage() * this.imagePageSize())
      .set('take',this.imagePageSize())
      .set('count', count)
      .set('order', 'modified:desc')
      .set('recordId', this.sample().id)
      .set('prefix', this.imagePrefix);

    return `${this.kinko}/dataset/${this.geologicalSampleDatasetId}/attachment/?${httpParams.toString()}`;
  });

  imagePageChange(event: PageEvent) {
    this.imagePage.set(event.pageIndex);
    this.imagePageSize.set(event.pageSize)
  }

  // General files
  filePrefix = '/sample/docs/'
  fileCount = signal(0);
  filePage = signal(0);
  fileDefaultPage = 5;
  filePageSize = signal(this.fileDefaultPage);

  // We make a separate request to get other files attached to the record.  We
  // use a regex query looking for a string that looks like '/.+/docs/$' rather
  // than the actual prefix. Since the prefix contains the expedition it's not
  // unlikely that someone edits it by accident or maybe an update fails and
  // the prefix goes out of sync. This way we have the best chance to return a
  // correct file list giving people to edit/see the mistakes.
  //
  // NOTE! On the item page we do use the prefix. Hopefully this makes it easy
  // to see to the user that they broke something that needs to be fixed when
  // their file disappears.
  fileList = httpResource<AttachmentListResponse>(() => {
    const count = untracked(() => this.fileCount() === 0 || this.filePage() === 0);
    const httpParams = new HttpParams()
      .set('skip', this.filePage() * this.imagePageSize())
      .set('take',this.filePageSize())
      .set('count', count)
      .set('order', 'modified:desc')
      .set('recordId', this.sample().id)
      .set('prefix', this.filePrefix);

    return `${this.kinko}/dataset/${this.geologicalSampleDatasetId}/attachment/?${httpParams.toString()}`;
  });

  filePageChange(event: PageEvent) {
    this.filePage.set(event.pageIndex);
    this.filePageSize.set(event.pageSize)
  }

  /////////////////
  // CONSTRUCTOR //
  /////////////////

  constructor() {
    untracked(() => {this.isDraft.set(this.sample().content?.draft)});
    untracked(() => {this.isFossil.set(this.sample().content?.fossil)});

    // Image count effect
    effect(() => {
      if (!this.imageList.hasValue() || !this.imageList.value().count) {
        return
      }
      this.imageCount.set(this.imageList.value().count);
    });

    // file count effect
    effect(() => {
      if (!this.fileList.hasValue() || !this.fileList.value().count) {
        return
      }
      this.fileCount.set(this.fileList.value().count);
    });

  }

  // Toggle draft state
  toggleDarft() {
    this.generalForm.markAsDirty();
    this.isDraft.set(!this.isDraft());
  }

  // Toggle fossil state
  toggleFossil() {
    this.generalForm.markAsDirty();
    this.isFossil.set(!this.isFossil());
  }

  //////////////////
  // FILE METHODS //
  //////////////////


  // Upload
  fileUpload(event: Event, prefix: string) {
    const element = event.target as HTMLInputElement;
    const files: FileList | null = element.files;

    if (!files) {
      return;
    }

    this.dialog.open(DatasetFormAttachmentCreateComponent, {
      data: {
        recordId: this.sample().id,
        datasetId: this.geologicalSampleDatasetId,
        files: files,
        prefix: prefix,
      },
      minWidth: '360px',
      width: '480px',
      disableClose: true
    }).afterClosed().subscribe(() => {
      element.value = '';
      if (prefix == this.imagePrefix) {
        this.imageList.reload();
      } else {
        this.fileList.reload();
      }
    });
  }

  // Edit metadata
  fileEdit(list: HttpResourceRef<AttachmentListResponse | undefined>, items: MatListOption[]) {
    const ids = items.map((o) => o.value as string);
    const attachments = list.value()?.items
      .filter((a) => ids.includes(a.id));

    if (!attachments?.length) {
      return;
    }

    this.dialog.open(DatasetFormAttachmentComponent, {
      data: attachments,
      minWidth: '360px',
      width: '480px',
      disableClose: true,
    }).afterClosed().subscribe((updatedAttachments: Attachment[]) => {
      if (!updatedAttachments) {
        return;
      }
      list.reload();
    });
  }

  // Delete
  fileDelete(list: HttpResourceRef<AttachmentListResponse | undefined>, items: MatListOption[]) {
    const ids = items.map((o) => o.value as string);
    const attachments = list.value()?.items
      .filter((a) => ids.includes(a.id));

    if (!attachments?.length) {
      return;
    }

    this.dialog.open(DatasetFormAttachmentDeleteComponent, {
      data: attachments,
      minWidth: '360px',
      width: '480px',
      disableClose: true,
    }).afterClosed().subscribe((deletedAttachments: Attachment[]) => {
      if (!deletedAttachments?.length) {
        return;
      }
      list.reload();
    });
  }

  ////////////////////
  // Geologist Form //
  ////////////////////

  addGeologist() {
    this.dialog.open(GeologicalSampleGeologistComponent, {
      width: '480px',
    }).afterClosed().subscribe((geologist) => {
      if (!geologist) {
        return;
      }

      const geologists = [ geologist ];
      const sample = this.sample();
      if (sample.content?.geologists?.length) {
        sample.content.geologists = [ ...sample.content.geologists , ...geologists ];
      } else {
        sample.content.geologists = geologists;
      }

      this.geologicalSampleService.updateSample(sample.id, sample).subscribe({
        next: () => {
          this.sample.set(sample);
          this.notificationService.info('Successfully saved changes.');
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to save changes.');
        },
      });
    });
  }

  removeGeologist(options: MatListOption[]) {
    const geologists = this.sample()?.content
      .geologists?.filter((v) => options.findIndex((o) => o.value === v) === -1);

    const content = this.sample()?.content

    // If no geologists remain delete the key from the content
    if (geologists?.length === 0) {
      delete content.geologists
    } else {
      content.geologists = geologists
    }

    const sample = this.sample();
    sample.content = { ...sample.content, ...content}
    this.geologicalSampleService.updateSample(sample.id, sample).subscribe({
      next: () => {
        this.sample.set(sample);
        this.notificationService.info('Successfully saved changes.');
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error('Failed to save changes.');
      },
    });
  }

  ///////////////
  // Link Form //
  ///////////////

  addLink() {
    this.dialog.open(GeologicalSampleLinkComponent, {
      width: '480px',
    }).afterClosed().subscribe((link) => {
      if (!link) {
        return;
      }

      const links = [ link ];
      const sample = this.sample();
      if (sample.content?.links?.length) {
        sample.content.links = [ ...sample.content.links , ...links ];
      } else {
        sample.content.links = links;
      }

      this.geologicalSampleService.updateSample(sample.id, sample).subscribe({
        next: () => {
          this.sample.set(sample);
          this.notificationService.info('Successfully saved changes.');
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to save changes.');
        },
      });
    });
  }

  editLink(option: MatListOption) {
    this.dialog.open(GeologicalSampleLinkComponent, {
      width: '480px',
      data: option?.value,
      disableClose: true,
    }).afterClosed().subscribe((link) => {
      if (!link) {
        return;
      }

      const sample = this.sample();
      const links = sample.content.links ? sample.content.links : [];

      if (option) {
        const index = sample.content.links?.indexOf(option.value);
        if (index !== undefined && index !== -1) {
          links[index] = link;
        }
      }
      sample.content.links = links;

      this.geologicalSampleService.updateSample(sample.id, sample).subscribe({
        next: () => {
          this.sample.set(sample);
          this.notificationService.info('Successfully saved changes.');
        },
        error: (err) => {
          console.error(err);
          this.notificationService.error('Failed to save changes.');
        },
      });
    })
  }

  removeLink(options: MatListOption[]) {
    const links = this.sample()?.content
      .links?.filter((v) => options.findIndex((o) => o.value === v) === -1);

    const content = this.sample()?.content

    // If no links remain delete the key from the content
    if (links?.length === 0) {
      delete content.links
    } else {
      content.links = links
    }

    const sample = this.sample();
    sample.content = { ...sample.content, ...content}
    this.geologicalSampleService.updateSample(sample.id, sample).subscribe({
      next: () => {
        this.sample.set(sample);
        this.notificationService.info('Successfully saved changes.');
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error('Failed to save changes.');
      },
    });
  }

  ///////////////////
  // Form Handlers //
  ///////////////////

  submit() {
    const sample = this.sample();

    // Store general form info when valid
    if (this.generalForm.valid) {
      const sampleDetails = {
        sampleName         : this.sample().content.sampleName,
        lithology          : this.generalForm.value.lithology?.trim(),
        description        : this.generalForm.value.description?.trim(),
        notes              : this.generalForm.value.notes?.trim(),
        updates            : this.generalForm.value.updates?.trim(),
        geologist          : this.generalForm.value.geologist?.trim(),
        expedition         : this.generalForm.value.expedition?.trim(),
        originalSampleName : this.generalForm.value.originalSampleName?.trim(),
        collectedYear      : this.generalForm.value.collectedYear?.trim(),
        cabinetRef         : this.generalForm.value.cabinetRef?.trim(),
        thinSectionRef     : this.generalForm.value.thinSectionRef?.trim(),
        draft              : this.isDraft(),
        fossil             : this.isFossil(),
      } as GeologicalSampleDetails;

      sample.content = { ...sample.content, ...sampleDetails };
    }

    // Store geo form info when valid

    if (this.geoForm.valid) {
      let coords: number[] = [];
      if (this.geoForm.value.longitude !== 0 && this.geoForm.value.latitude !== 0) {
        coords = [
          this.geoForm.value.longitude,
          this.geoForm.value.latitude,
          this.geoForm.value.altitude
        ]
      }

      let geoj = this.sample().content?.geojson;
      if (coords.length === 3) {
        geoj = {
          type: 'FeatureCollection',
          features: [{
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'Point',
              coordinates: coords
            }
          }]
        };
      }

      const geoDetails = {
        placename: this.geoForm.value.placename?.trim(),
        geojson: geoj,
      } as GeologicalSampleDetails;

      sample.content = { ...sample.content, ...geoDetails };
    }

    this.geologicalSampleService.updateSample(sample.id, sample).subscribe({
      next: () => {
        this.sample.set(sample);
        this.generalForm.markAsPristine();
        this.notificationService.info('Successfully saved changes.');
      },
      error: (err) => {
        console.error(err);
        this.notificationService.error('Failed to save changes.');
      },
    });
  }

  close() {
    const pristine = this.generalForm.pristine;

    if (!pristine) {
      this.dialog.open(PromptComponent, {
        minWidth: '360px',
        width: '480px',
        data: {
          title: 'Unsaved data',
          content: 'If you continue these changes will be discarded.'
        }
      }).afterClosed().subscribe((result: boolean) => {
        if (result) {
          this.router.navigate(['/geology/sample/', this.sample().id]);
        }
      });
    } else {
      this.router.navigate(['/geology/sample/', this.sample().id]);
    }
  }
}
